// Source-attribution helpers for the Messages "Scheduled" tab.
//
// Scheduled outbound messages come from two kinds of source:
//   • a messaging rule (rule-generated) — carries a `ruleId` (and usually a
//     joined `ruleName`), OR
//   • a one-off manual send — `ruleId == null`.
//
// A scheduled message keeps its rule attribution even after the originating
// rule is *disabled* (the rule row still exists, so the left join still
// resolves its name). Disabling a scheduled rule does NOT remove the messages
// it already materialized for the next 7 days — they still send — so hosts need
// to be able to find every message from a given rule (enabled or disabled) to
// cancel the ones they don't want. These helpers power that source filter and
// the disable-confirmation warning, and are kept pure so they can be unit
// tested without the DOM.

export type ScheduleSourceRow = {
  ruleId: number | null;
  ruleName: string | null;
  // Messages Auto Draft — "auto_draft" rows have no rule but are not manual.
  automationSource?: string | null;
  autoSend?: boolean | null;
  sendAt?: string | Date | null;
};

export type ScheduleSourceOption = {
  ruleId: number;
  ruleName: string | null;
  label: string;
  count: number;
};

export type ScheduleSources = {
  total: number;
  manualCount: number;
  autoDraftCount: number;
  rules: ScheduleSourceOption[];
};

/** Source-filter value for AI auto-drafts (Messages Auto Draft beta). */
export const AUTO_DRAFT_SOURCE_VALUE = "auto_draft";

export function isAutoDraftRow(r: { automationSource?: string | null }): boolean {
  return r.automationSource === "auto_draft";
}

export function ruleSourceValue(ruleId: number): string {
  return `rule:${ruleId}`;
}

export function ruleDisplayLabel(ruleId: number, ruleName: string | null): string {
  const name = ruleName?.trim();
  return name && name.length > 0 ? name : `Rule #${ruleId}`;
}

// Build the distinct list of sources present in the given scheduled rows.
// Rules are sorted by display label so the dropdown order is stable. Disabled
// (and even deleted) rules are included as long as at least one of their
// messages is still scheduled, because their rows persist in the data.
export function deriveScheduleSources(rows: ScheduleSourceRow[]): ScheduleSources {
  let manualCount = 0;
  let autoDraftCount = 0;
  const byRule = new Map<number, ScheduleSourceOption>();
  for (const r of rows) {
    if (isAutoDraftRow(r)) {
      autoDraftCount += 1;
      continue;
    }
    if (r.ruleId == null) {
      manualCount += 1;
      continue;
    }
    const existing = byRule.get(r.ruleId);
    if (existing) {
      existing.count += 1;
      // Prefer the first non-empty name we encounter (joins can be null on
      // some rows for the same rule).
      if ((!existing.ruleName || existing.ruleName.trim() === "") && r.ruleName) {
        existing.ruleName = r.ruleName;
        existing.label = ruleDisplayLabel(r.ruleId, r.ruleName);
      }
    } else {
      byRule.set(r.ruleId, {
        ruleId: r.ruleId,
        ruleName: r.ruleName,
        label: ruleDisplayLabel(r.ruleId, r.ruleName),
        count: 1,
      });
    }
  }
  const rules = Array.from(byRule.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
  return { total: rows.length, manualCount, autoDraftCount, rules };
}

// Apply the selected source filter. `sourceFilter` is one of:
//   "all"           → every row
//   "manual"        → rows with no rule attribution (excluding auto-drafts)
//   "auto_draft"    → AI auto-drafts
//   "rule:<id>"     → rows from that specific rule (enabled or disabled)
export function applyScheduleSourceFilter<T extends ScheduleSourceRow>(
  rows: T[],
  sourceFilter: string,
): T[] {
  if (!sourceFilter || sourceFilter === "all") return rows;
  if (sourceFilter === AUTO_DRAFT_SOURCE_VALUE) return rows.filter(isAutoDraftRow);
  if (sourceFilter === "manual") return rows.filter((r) => r.ruleId == null && !isAutoDraftRow(r));
  const match = /^rule:(\d+)$/.exec(sourceFilter);
  if (match) {
    const id = Number(match[1]);
    return rows.filter((r) => r.ruleId === id);
  }
  return rows;
}

// Count the messages a scheduled rule has already queued that will still be
// sent (auto-send, future send time). Drives the disable-confirmation warning.
export function countFutureScheduledForRule(
  rows: ScheduleSourceRow[],
  ruleId: number,
  now: number = Date.now(),
): number {
  return rows.filter(
    (r) =>
      r.ruleId === ruleId &&
      r.autoSend === true &&
      r.sendAt != null &&
      new Date(r.sendAt).getTime() > now,
  ).length;
}
